import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Users, CalendarDays, Package, Briefcase, FileCheck, Camera,
  Clapperboard, ClipboardCheck, ClipboardList, Target, LogOut, ChevronLeft, ChevronRight,
  LayoutDashboard, Star, FileText, TrendingUp, Clock, BarChart3, ScrollText, Menu, X, KeyRound,
  Home, Wallet, Calendar, UserCircle, ChevronDown, Sparkles, Zap
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

import API from '../utils/api';
import TrendingPulse from './TrendingPulse';
import DailyLearning from './DailyLearning';

const ADMIN_NAV_SECTIONS = [
  {
    title: 'Home',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { label: 'Daily Brief', path: '/daily-brief', icon: Sparkles },
      { label: 'Analytics', path: '/analytics', icon: BarChart3 },
      { label: 'Approvals', path: '/approvals', icon: ClipboardCheck, badgeKey: 'approvals' },
    ],
  },
  {
    title: 'Work',
    items: [
      { label: 'Production Board', path: '/production', icon: Clapperboard },
      { label: 'Execution Board', path: '/execution', icon: ClipboardList },
      { label: 'Performance Board', path: '/performance', icon: Target },
      { label: 'Business Dev', path: '/business-dev', icon: Zap },
      { label: 'Shoots', path: '/shoots', icon: Camera },
      { label: 'Deliverables', path: '/deliverables', icon: FileCheck },
      { label: 'Attendance', path: '/attendance', icon: CalendarDays },
      { label: 'Punch Tracker', path: '/punch-tracker', icon: Clock },
    ],
  },
  {
    title: 'Records',
    items: [
      { label: 'Clients', path: '/clients', icon: Briefcase },
      { label: 'Employees', path: '/employees', icon: Users },
      { label: 'Talent', path: '/talent', icon: Star },
      { label: 'Assets', path: '/assets', icon: Package },
      { label: 'Audit Logs', path: '/audit-logs', icon: ScrollText },
    ],
  },
  {
    title: 'Finance',
    items: [
      { label: 'Invoices', path: '/invoices', icon: FileText },
      { label: 'Finance', path: '/finance', icon: TrendingUp },
      { label: 'Payroll', path: '/payroll', icon: Wallet },
    ],
  },
];

const TRACKER_ICON_MAP = {
  production: Clapperboard, shoots: Camera, clients: Briefcase,
  deliverables: FileCheck, attendance: CalendarDays, assets: Package, talent: Star,
  execution: ClipboardList,
  performance: Target,
  approvals: ClipboardCheck, invoices: FileText,
  business_dev: Zap,
};

const TRACKER_LABEL_MAP = {
  production: 'Production Board', shoots: 'Shoots', clients: 'Clients',
  deliverables: 'Deliverables', attendance: 'Attendance', assets: 'Assets', talent: 'Talent',
  execution: 'Execution Board',
  performance: 'Performance Board',
  approvals: 'Approvals', invoices: 'Invoices',
  business_dev: 'Business Dev',
};

const TRACKER_PATH_MAP = {
  production: '/production', shoots: '/shoots', clients: '/clients',
  deliverables: '/deliverables', attendance: '/attendance', assets: '/assets', talent: '/talent',
  execution: '/execution',
  performance: '/performance',
  approvals: '/approvals', invoices: '/invoices',
  business_dev: '/business-dev',
};

// Only these trackers have valid employee-accessible routes (PermRoute in App.js)
const VALID_EMPLOYEE_TRACKERS = new Set(['production', 'shoots', 'clients', 'deliverables', 'attendance', 'assets', 'talent', 'execution', 'performance', 'approvals', 'invoices', 'business_dev']);

function buildEmployeeNav(effectivePermissions) {
  const perms = effectivePermissions || {};
  const trackerItems = Object.keys(perms)
    .filter(k => VALID_EMPLOYEE_TRACKERS.has(k) && perms[k] && perms[k] !== 'none')
    .map(k => ({ label: TRACKER_LABEL_MAP[k] || k, path: TRACKER_PATH_MAP[k] || `/${k}`, icon: TRACKER_ICON_MAP[k] || Briefcase }));

  const sections = [
    { title: '', items: [{ label: 'Home', path: '/employee', icon: Home }] },
  ];
  if (trackerItems.length > 0) {
    sections.push({ title: 'Trackers', items: trackerItems });
  }
  sections.push(
    { title: '', items: [
      { label: 'Reimbursements', path: '/employee?tab=reimbursements', icon: Wallet },
      { label: 'Leave', path: '/employee?tab=leave', icon: Calendar },
      { label: 'Profile', path: '/employee?tab=profile', icon: UserCircle },
    ]}
  );
  return sections;
}

export default function SidebarLayout({ children, title, subtitle }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [expanded, setExpanded] = useState({ Boards: true });
  const [badges, setBadges] = useState({ approvals: { pending: 0, flagged: 0 } });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Auto-expand Boards when on a board route
  useEffect(() => {
    if (['/production', '/execution', '/performance'].some(p => location.pathname.startsWith(p))) {
      setExpanded(e => ({ ...e, Boards: true }));
    }
  }, [location.pathname]);

  // Live badges for admin — poll approvals stats
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(`${API}/approvals/stats`, { withCredentials: true });
        if (!cancelled) setBadges(b => ({ ...b, approvals: { pending: data.total_pending || 0, flagged: data.flagged || 0 } }));
      } catch { /* silent */ }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.role, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const NAV_SECTIONS = isAdmin ? ADMIN_NAV_SECTIONS : buildEmployeeNav(user?.effective_permissions);

  const isActive = (path) => {
    if (path.includes('?tab=')) {
      return location.pathname + location.search === path;
    }
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    if (path === '/employee') {
      return location.pathname === '/employee' && !location.search;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const sidebarContent = (isMobile) => (
    <>
      {/* Logo */}
      <div className={`px-4 py-5 border-b border-white/5 flex items-center ${!isMobile && collapsed ? 'justify-center' : 'justify-between'}`}>
        {(isMobile || !collapsed) && (
          <h1 className="font-heading text-sm font-black tracking-tighter text-white leading-none" data-testid="sidebar-logo">
            Scale Sense<br /><span className="text-[10px] text-slate-500 font-normal tracking-wide">HQ</span>
          </h1>
        )}
        {isMobile ? (
          <button onClick={() => setMobileOpen(false)}
            className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-close">
            <X size={18} />
          </button>
        ) : (
          <button onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/5 transition-colors" data-testid="sidebar-toggle">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {NAV_SECTIONS.map((section, sIdx) => (
          <div key={section.title || `section-${sIdx}`}>
            {(isMobile || !collapsed) && (
              <p className="text-[9px] font-semibold tracking-[0.2em] uppercase text-slate-600 px-2 mb-1.5">{section.title}</p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;

                // Parent with collapsible children (e.g. Boards)
                if (item.children) {
                  const anyChildActive = item.children.some(c => isActive(c.path));
                  const isOpen = !!expanded[item.label];
                  return (
                    <div key={item.label}>
                      <button
                        onClick={() => {
                          if (!isMobile && collapsed) {
                            // In collapsed mode, clicking navigates to first child
                            handleNav(item.children[0].path);
                          } else {
                            setExpanded(e => ({ ...e, [item.label]: !e[item.label] }));
                          }
                        }}
                        data-testid={`nav-group-${item.label.toLowerCase()}`}
                        className={`w-full flex items-center gap-2.5 rounded-lg transition-all duration-200 group ${!isMobile && collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'} ${anyChildActive ? 'text-white' : 'text-slate-500 hover:text-slate-200 hover:bg-white/[0.04]'} border border-transparent`}
                        title={!isMobile && collapsed ? item.label : undefined}
                      >
                        <Icon size={16} className={`shrink-0 ${anyChildActive ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                        {(isMobile || !collapsed) && (
                          <>
                            <span className={`text-xs font-medium flex-1 text-left ${anyChildActive ? 'text-white' : ''}`}>{item.label}</span>
                            <ChevronDown size={12} className={`transition-transform ${isOpen ? '' : '-rotate-90'} ${anyChildActive ? 'text-indigo-400' : 'text-slate-600'}`} />
                          </>
                        )}
                      </button>
                      {isOpen && (isMobile || !collapsed) && (
                        <div className="ml-3 pl-3 border-l border-white/5 mt-0.5 space-y-0.5">
                          {item.children.map(child => {
                            const ChildIcon = child.icon;
                            const active = isActive(child.path);
                            return (
                              <button
                                key={child.label}
                                onClick={() => handleNav(child.path)}
                                data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`w-full flex items-center gap-2.5 rounded-lg transition-all duration-200 group px-3 py-1.5 ${active
                                  ? 'bg-indigo-600/15 text-white border border-indigo-500/20'
                                  : 'text-slate-500 hover:text-slate-200 hover:bg-white/[0.04] border border-transparent'
                                  }`}
                              >
                                <ChildIcon size={13} className={`shrink-0 ${active ? 'text-indigo-400' : 'text-slate-600 group-hover:text-slate-400'}`} />
                                <span className={`text-xs font-medium ${active ? 'text-white' : ''}`}>{child.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Leaf item with optional badges
                const active = isActive(item.path);
                const badge = item.badgeKey ? badges[item.badgeKey] : null;
                const pending = badge?.pending || 0;
                const flagged = badge?.flagged || 0;
                const hasBadge = pending > 0 || flagged > 0;

                return (
                  <button key={item.label} onClick={() => handleNav(item.path)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full flex items-center gap-2.5 rounded-lg transition-all duration-200 group ${!isMobile && collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'} ${active
                        ? 'bg-indigo-600/15 text-white border border-indigo-500/20'
                        : 'text-slate-500 hover:text-slate-200 hover:bg-white/[0.04] border border-transparent'
                      }`}
                    title={!isMobile && collapsed ? `${item.label}${hasBadge ? ` (${pending + flagged})` : ''}` : undefined}
                  >
                    <div className="relative shrink-0">
                      <Icon size={16} className={`${active ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                      {!isMobile && collapsed && hasBadge && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-500 ring-1 ring-[#08080f]" />
                      )}
                    </div>
                    {(isMobile || !collapsed) && (
                      <>
                        <span className={`text-xs font-medium flex-1 text-left ${active ? 'text-white' : ''}`}>{item.label}</span>
                        {pending > 0 && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/25 min-w-[18px] text-center" data-testid={`nav-badge-pending-${item.label.toLowerCase()}`}>
                            {pending > 99 ? '99+' : pending}
                          </span>
                        )}
                        {flagged > 0 && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25 min-w-[18px] text-center" data-testid={`nav-badge-flagged-${item.label.toLowerCase()}`}>
                            {flagged > 99 ? '99+' : flagged}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User + Logout */}
      <div className={`px-3 py-4 border-t border-white/5 ${!isMobile && collapsed ? 'flex flex-col items-center gap-1' : ''}`}>
        {(isMobile || !collapsed) && (
          <div className="flex items-center gap-2 mb-3 px-1">
            <div className="w-7 h-7 rounded-full bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center">
              <span className="text-[10px] font-bold text-indigo-400">{user?.name?.charAt(0)}</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-white truncate font-medium">{user?.name}</p>
              <p className="text-[10px] text-slate-500 truncate">{user?.role}</p>
            </div>
          </div>
        )}
        {user?.role === 'admin' && (
          <button onClick={() => setShowChangePw(true)} data-testid="change-password-button"
            className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/5 transition-colors mb-0.5 ${!isMobile && collapsed ? 'justify-center' : ''}`}>
            <KeyRound size={16} />
            {(isMobile || !collapsed) && <span className="text-xs">Change Password</span>}
          </button>
        )}
        <button onClick={handleLogout} data-testid="logout-button"
          className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-slate-500 hover:text-rose-400 hover:bg-rose-500/5 transition-colors ${!isMobile && collapsed ? 'justify-center' : ''}`}>
          <LogOut size={16} />
          {(isMobile || !collapsed) && <span className="text-xs">Sign Out</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#05050A] flex">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex fixed top-0 left-0 h-full z-50 flex-col border-r border-white/5 bg-[#08080f]/95 backdrop-blur-xl transition-all duration-300 ${collapsed ? 'w-[60px]' : 'w-[220px]'}`}
        data-testid="sidebar"
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-sidebar-overlay">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-10 w-[260px] h-full flex flex-col border-r border-white/5 bg-[#08080f] overflow-y-auto">
            {sidebarContent(true)}
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 md:${collapsed ? 'ml-[60px]' : 'ml-[220px]'}`}
        style={{ marginLeft: typeof window !== 'undefined' && window.innerWidth >= 768 ? (collapsed ? 60 : 220) : 0 }}>
        {/* Page Header */}
        <header className={`${title ? 'border-b border-white/5' : ''} bg-[#05050A]/80 backdrop-blur-xl sticky top-0 z-40 px-4 md:px-8 py-4 md:py-5 flex items-center gap-3`}>
          <button onClick={() => setMobileOpen(true)}
            className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="mobile-menu-button">
            <Menu size={20} />
          </button>
          {title && (
            <div>
              <h2 className="font-heading text-lg md:text-xl font-bold text-white" data-testid="page-title">{title}</h2>
              {subtitle && <p className="text-slate-500 text-xs md:text-sm mt-0.5">{subtitle}</p>}
            </div>
          )}
          <div className="flex-1" />
          <DailyLearning />
          <TrendingPulse />
        </header>
        <div className="px-4 md:px-8 py-4 md:py-6">
          {children}
        </div>
      </main>

      {/* Change Password Modal */}
      {showChangePw && (
        <ChangePasswordModal onClose={() => setShowChangePw(false)} />
      )}
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (newPw.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (newPw !== confirmPw) {
      setError('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPw,
        new_password: newPw,
      }, { withCredentials: true });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 max-w-md" data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-xl">Change Password</DialogTitle>
        </DialogHeader>
        {success ? (
          <div className="py-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center mx-auto mb-3">
              <KeyRound size={22} className="text-emerald-400" />
            </div>
            <p className="text-emerald-400 font-medium">Password changed successfully!</p>
            <p className="text-slate-500 text-sm mt-1">Use your new password on next login.</p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {error && (
              <div className="bg-rose-500/10 border border-rose-500/20 rounded px-3 py-2">
                <p className="text-rose-400 text-sm" data-testid="change-pw-error">{error}</p>
              </div>
            )}
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Current Password</label>
              <Input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="bg-black/40 border-white/10 text-white"
                data-testid="current-password-input"
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">New Password</label>
              <Input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="bg-black/40 border-white/10 text-white"
                data-testid="new-password-input"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Confirm New Password</label>
              <Input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="bg-black/40 border-white/10 text-white"
                data-testid="confirm-password-input"
                placeholder="Re-enter new password"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            data-testid="cancel-change-pw"
          >
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button
              onClick={handleSubmit}
              disabled={loading || !currentPw || !newPw || !confirmPw}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="submit-change-pw"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
